import type { TTMLLyric } from "$/types/ttml.ts";

export type CollaborationMessage =
	| { type: "hello"; clientId: string }
	| { type: "update"; clientId: string; revision: number; document: TTMLLyric }
	| {
			type: "presence";
			clientId: string;
			name: string;
			mode: string;
			version: string;
			host?: boolean;
	  }
	| {
			type: "audio-start";
			clientId: string;
			audioId: string;
			mime: string;
			size: number;
	  }
	| {
			type: "audio-chunk";
			clientId: string;
			audioId: string;
			index: number;
			data: string;
	  }
	| { type: "audio-end"; clientId: string; audioId: string }
	| { type: "close-room"; clientId: string }
	| { type: "rename-room"; clientId: string; name: string };

export interface CollaborationPeer {
	clientId: string;
	name: string;
	mode: string;
	version: string;
	host?: boolean;
}

export interface CollaborationRoomListing {
	id: string;
	name: string;
	visibility: "public" | "private";
	createdAt: number;
	users?: number;
}

export type CollaborationServerMessage =
	| {
			type: "welcome";
			clientId: string;
			revision: number;
			document: TTMLLyric | null;
			peers: CollaborationPeer[];
			hostClientId?: string | null;
			roomName?: string;
	  }
	| { type: "update"; clientId: string; revision: number; document: TTMLLyric }
	| {
			type: "presence";
			peers: CollaborationPeer[];
			hostClientId?: string | null;
	  }
	| { type: "room-closed" }
	| { type: "room-renamed"; name: string }
	| { type: "audio-request"; target: string }
	| {
			type: "audio-start";
			clientId: string;
			audioId: string;
			mime: string;
			size: number;
	  }
	| {
			type: "audio-chunk";
			clientId: string;
			audioId: string;
			index: number;
			data: string;
	  }
	| { type: "audio-end"; clientId: string; audioId: string }
	| { type: "error"; message: string };

export const makeClientId = () => {
	const storageKey = "amll-collaboration-client-id";
	try {
		const existing = sessionStorage.getItem(storageKey);
		if (existing) return existing;
	} catch {}
	if (typeof crypto !== "undefined" && "randomUUID" in crypto)
		return persistClientId(crypto.randomUUID(), storageKey);
	return persistClientId(
		`${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`,
		storageKey,
	);
};

const persistClientId = (clientId: string, storageKey: string) => {
	try {
		sessionStorage.setItem(storageKey, clientId);
	} catch {}
	return clientId;
};

export const makeRoomKey = () => {
	const bytes = new Uint8Array(24);
	crypto.getRandomValues(bytes);
	return btoa(String.fromCharCode(...bytes))
		.replace(/[^a-zA-Z0-9]/g, "")
		.slice(0, 32);
};

export const getCollaborationUrl = (
	room: string,
	key: string,
	clientId: string,
	version: string,
) => {
	const configured =
		import.meta.env.VITE_COLLABORATION_URL?.trim() ||
		"wss://amll-ttml-collaboration.proxygroupp.workers.dev";
	const base = configured
		? `${configured.replace(/\/$/, "")}/${encodeURIComponent(room)}`
		: `${window.location.protocol === "https:" ? "wss:" : "ws:"}//${window.location.host}/api/collab/${encodeURIComponent(room)}`;
	return `${base}?key=${encodeURIComponent(key)}&clientId=${encodeURIComponent(clientId)}&version=${encodeURIComponent(version)}`;
};

export const getCollaborationApiUrl = () => {
	const configured =
		import.meta.env.VITE_COLLABORATION_URL?.trim() ||
		"https://amll-ttml-collaboration.proxygroupp.workers.dev";
	return configured.replace(/^wss:/, "https:").replace(/^ws:/, "http:");
};

export const listPublicRooms = async (): Promise<
	CollaborationRoomListing[]
> => {
	const response = await fetch(`${getCollaborationApiUrl()}/rooms`);
	if (!response.ok) throw new Error("Could not load public rooms");
	return response.json();
};

export const createRoom = async (
	name: string,
	visibility: "public" | "private",
) => {
	const response = await fetch(`${getCollaborationApiUrl()}/rooms`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ name, visibility }),
	});
	if (!response.ok) throw new Error("Could not create room");
	return response.json() as Promise<
		CollaborationRoomListing & { ownerKey: string }
	>;
};

export const renameRoom = async (
	id: string,
	name: string,
	ownerKey: string,
) => {
	const response = await fetch(
		`${getCollaborationApiUrl()}/rooms/${encodeURIComponent(id)}`,
		{
			method: "PATCH",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ name, ownerKey }),
		},
	);
	if (!response.ok) throw new Error("Only the room owner can rename this room");
};
